// src/components/fees/FeePayments.js
import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Grid, Dialog, DialogTitle, DialogContent, DialogActions, Chip,
  Skeleton, TablePagination, Stack, IconButton, Tooltip, Alert, Autocomplete, Pagination
} from '@mui/material';
import { 
  Add as AddIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  FilterAlt as FilterIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { feeAPI, studentAPI, accountingAPI, academicAPI } from '../../services/api';
import { useAccessControl, RESOURCES, ACTIONS } from '../../hooks/useAccessControl';

const FeePayments = () => {
  const { can } = useAccessControl();
  const canRead = can(RESOURCES.FEES.PAYMENTS, ACTIONS.READ);
  const canCreate = can(RESOURCES.FEES.PAYMENTS, ACTIONS.CREATE);
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [feeGroups, setFeeGroups] = useState([]);
  const [studentFeeGroups, setStudentFeeGroups] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    student_id: '', fee_group_id: '', amount: '', payment_method: 'cash',
    payment_date: new Date().toISOString().split('T')[0], remarks: '', account_id: ''
  });
  const [studentSearchOptions, setStudentSearchOptions] = useState([]);
  const [studentSearchLoading, setStudentSearchLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentSearchInput, setStudentSearchInput] = useState('');
  const [loadingStudentFeeGroups, setLoadingStudentFeeGroups] = useState(false);
  
  // Pagination and filters
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filters, setFilters] = useState({
    student_id: '',
    payment_method: '',
    from_date: '',
    to_date: '',
    term_id: ''
  });
  const [downloading, setDownloading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (canRead) {
      fetchTerms();
      fetchPayments();
      fetchStudents();
      fetchFeeGroups();
      fetchAccounts();
    }
  }, [canRead]);

  useEffect(() => {
    fetchPayments(1);
  }, [filters]);

  const fetchTerms = async () => {
    try {
      const response = await academicAPI.getTerms();
      setTerms(Array.isArray(response.data) ? response.data : response.data.data || []);
    } catch (error) {
      console.error('Error fetching terms:', error);
    }
  };

  const fetchPayments = async (pageNum = page, filterParams = filters, perPage = pageSize) => {
    console.log('Fetching payments with params:', { page: pageNum, per_page: perPage, ...filterParams });
    try {
      const params = { page: pageNum, per_page: perPage, ...filterParams };
      const response = await feeAPI.getPayments(params);
      console.log('Full API response:', response);
      
      const paymentsData = response.data.data || response.data || [];
      console.log('Extracted payments data:', paymentsData);
      
      setPayments(Array.isArray(paymentsData) ? paymentsData : []);
      setTotalPages(response.data.last_page || 1);
      setTotalRecords(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching payments:', error);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await studentAPI.getActive();
      const studentsData = response.data.data || response.data || [];
      setStudents(Array.isArray(studentsData) ? studentsData : []);
    } catch (error) {
      console.error('Error fetching students:', error);
      setStudents([]);
    }
  };

  const fetchFeeGroups = async () => {
    try {
      const response = await feeAPI.getGroups({ per_page: 1000 });
      const groupsData = response.data.data || response.data || [];
      const validGroups = Array.isArray(groupsData) ? groupsData : [];
      console.log('All fee groups fetched:', validGroups.length);
      setFeeGroups(validGroups);
    } catch (error) {
      console.error('Error fetching fee groups:', error);
      setFeeGroups([]);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await accountingAPI.getAccounts();
      const accountsData = response.data.data || response.data || [];
      setAccounts(Array.isArray(accountsData) ? accountsData : []);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setAccounts([]);
    }
  };

  if (!canRead) return <Alert severity="error">You do not have permission to view fee payments.</Alert>;

  if (loading) {
    return (
      <Box>
        {/* Header Skeleton */}
        <Box mb={4}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item xs={12} md={8}>
              <Skeleton variant="text" width={150} height={32} sx={{ mb: 1 }} />
              <Skeleton variant="text" width={180} height={20} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} mt={{ xs: 2, md: 0 }}>
                <Skeleton variant="rectangular" width={150} height={36} sx={{ borderRadius: 1 }} />
              </Box>
            </Grid>
          </Grid>
        </Box>
        
        {/* Form Card Skeleton */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Skeleton variant="text" width={200} height={24} sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        
        {/* Payment History Table Skeleton */}
        <Card>
          <CardContent>
            <Skeleton variant="text" width={140} height={24} sx={{ mb: 2 }} />
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    {['Student', 'Fee Group', 'Amount', 'Method', 'Date', 'Remarks'].map((header, i) => (
                      <TableCell key={i}>
                        <Skeleton variant="text" width={80} height={16} />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...Array(6)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton variant="text" width={120} height={16} /></TableCell>
                      <TableCell><Skeleton variant="text" width={100} height={16} /></TableCell>
                      <TableCell><Skeleton variant="text" width={80} height={16} /></TableCell>
                      <TableCell><Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: 3 }} /></TableCell>
                      <TableCell><Skeleton variant="text" width={80} height={16} /></TableCell>
                      <TableCell><Skeleton variant="text" width={100} height={16} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitting(true);
    
    try {
      await feeAPI.createPayment(formData);
      fetchPayments();
      fetchFeeGroups();
      handleClearForm();
      setShowForm(false);
    } catch (error) {
      console.error('Error creating payment:', error);
      if (error.response?.status === 422 && error.response?.data?.details) {
        const details = error.response.data.details;
        setSubmitError(
          `Payment exceeds allocated amount. Allocated: UGX ${details.allocated_amount?.toLocaleString()}, ` +
          `Already Paid: UGX ${details.already_paid?.toLocaleString()}, ` +
          `Remaining: UGX ${details.remaining_balance?.toLocaleString()}`
        );
      } else {
        setSubmitError(error.response?.data?.message || 'Failed to record payment. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const fetchStudentFeeGroups = async (studentId) => {
    if (!studentId) {
      setStudentFeeGroups([]);
      return;
    }

    setLoadingStudentFeeGroups(true);
    try {
      const response = await feeAPI.getStudentFeeGroups({ student_id: studentId });
      const groups = response.data.fee_groups || [];
      console.log('Fetched fee groups for student:', groups);
      setStudentFeeGroups(groups);
    } catch (error) {
      console.error('Error fetching student fee groups:', error);
      setStudentFeeGroups([]);
    } finally {
      setLoadingStudentFeeGroups(false);
    }
  };

  const getStudentFeeGroups = () => {
    return studentFeeGroups;
  };

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    setPage(1);
    fetchPayments(1, newFilters, pageSize);
  };

  const handlePageSizeChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    setPage(1);
    fetchPayments(1, filters, newPageSize);
  };

  const handleClearFilters = () => {
    const clearedFilters = { student_id: '', payment_method: '', from_date: '', to_date: '', term_id: '' };
    setFilters(clearedFilters);
    setPage(1);
    fetchPayments(1, clearedFilters, pageSize);
  };



  const handleDownloadPdf = async () => {
    try {
      setDownloading(true);
      const response = await feeAPI.downloadPaymentsPdf(filters);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fee_payments_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleClearForm = () => {
    setFormData({
      student_id: '', fee_group_id: '', amount: '', payment_method: 'cash',
      payment_date: new Date().toISOString().split('T')[0], remarks: '', account_id: ''
    });
    setSelectedStudent(null);
    setStudentSearchInput('');
    setStudentSearchOptions([]);
    setStudentFeeGroups([]);
    setSubmitError('');
  };

  const searchStudents = async (searchTerm) => {
    setStudentSearchInput(searchTerm);
    
    if (!searchTerm || searchTerm.length < 2) {
      setStudentSearchOptions([]);
      return;
    }

    setStudentSearchLoading(true);
    try {
      const response = await studentAPI.search({ search: searchTerm });
      const students = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setStudentSearchOptions(students);
    } catch (error) {
      console.error('Error searching students:', error);
      setStudentSearchOptions([]);
    } finally {
      setStudentSearchLoading(false);
    }
  };

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    setFormData({ ...formData, student_id: student?.id || '', fee_group_id: '' });
    
    // Fetch fee groups for the selected student
    if (student?.id) {
      fetchStudentFeeGroups(student.id);
    } else {
      setStudentFeeGroups([]);
    }
  };

  const hasActiveFilters = filters.student_id || filters.payment_method || filters.from_date || filters.to_date || filters.term_id;

  return (
    <Box>
      <Box mb={4}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" gutterBottom>Fee Payments</Typography>
            <Typography variant="body1" color="text.secondary">
              Record fee payments
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} gap={1} mt={{ xs: 2, md: 0 }}>
              {canRead && <Button variant="outlined" startIcon={<PdfIcon />} onClick={handleDownloadPdf} disabled={downloading}>{downloading ? 'Downloading...' : 'Download PDF'}</Button>}
              {canCreate && (
                <Button variant={showForm ? "outlined" : "contained"} startIcon={<AddIcon />} onClick={() => setShowForm(!showForm)}>
                  {showForm ? 'Cancel' : 'Record Payment'}
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Dialog open={showForm} onClose={() => { setShowForm(false); handleClearForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle>Record Fee Payment</DialogTitle>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} sx={{ pt: 1 }}>
            <Autocomplete
              fullWidth
              options={Array.isArray(studentSearchOptions) ? studentSearchOptions : []}
              value={selectedStudent}
              inputValue={studentSearchInput}
              onChange={(event, newValue) => handleStudentSelect(newValue)}
              onInputChange={(event, newInputValue) => searchStudents(newInputValue)}
              loading={studentSearchLoading}
              getOptionLabel={(option) => option ? `${option.registration_no} - ${option.full_name}` : ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      {option.registration_no}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.full_name} ({option.class_stream_name})
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Student"
                  placeholder="Type name or registration number..."
                  required
                  margin="normal"
                  helperText="Type at least 2 characters to search"
                />
              )}
              noOptionsText={studentSearchInput.length < 2 ? "Type at least 2 characters" : "No students found"}
              loadingText="Searching..."
              filterOptions={(x) => x}
            />
            <FormControl fullWidth required disabled={!selectedStudent || loadingStudentFeeGroups} margin="normal">
              <InputLabel>Fee Group</InputLabel>
              <Select
                value={formData.fee_group_id}
                label="Fee Group"
                onChange={(e) => setFormData({...formData, fee_group_id: e.target.value})}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                {loadingStudentFeeGroups ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      Loading fee groups...
                    </Typography>
                  </MenuItem>
                ) : getStudentFeeGroups().length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      {selectedStudent ? 'No fee groups available for this student' : 'Select a student first'}
                    </Typography>
                  </MenuItem>
                ) : (
                  getStudentFeeGroups().map((group) => (
                    <MenuItem 
                      key={group.id} 
                      value={group.id}
                      sx={{ py: 1.5 }}
                    >
                      <Box>
                        <Typography variant="body1" fontWeight={500}>
                          {group.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total: UGX {group.total_amount?.toLocaleString() || '0'}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Amount (UGX)"
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({...formData, amount: e.target.value})}
              required
              margin="normal"
            />
            <FormControl fullWidth margin="normal">
              <Select
                value={formData.payment_method}
                label="Payment Method"
                onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                displayEmpty
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="mobile_money">Mobile Money</MenuItem>
                <MenuItem value="bank">Bank</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Payment Date"
              type="date"
              value={formData.payment_date}
              onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
              required
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth required margin="normal">
              <InputLabel>Account</InputLabel>
              <Select
                value={formData.account_id}
                label="Account"
                onChange={(e) => setFormData({...formData, account_id: e.target.value})}
              >
                <MenuItem value="">Select Account</MenuItem>
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name} ({account.account_type})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Remarks"
              multiline
              rows={3}
              value={formData.remarks}
              onChange={(e) => setFormData({...formData, remarks: e.target.value})}
              placeholder="Optional payment notes..."
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowForm(false); handleClearForm(); }} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
            {submitting ? 'Recording...' : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <FilterIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">Filters</Typography>
          </Box>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <Select
                  value={filters.student_id}
                  label="Student"
                  onChange={(e) => handleFilterChange('student_id', e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">All Students</MenuItem>
                  {students.map((student) => (
                    <MenuItem key={student.id} value={student.id}>
                      {student.last_name} {student.first_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <Select
                  value={filters.payment_method}
                  label="Payment Method"
                  onChange={(e) => handleFilterChange('payment_method', e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">All Methods</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="mobile_money">Mobile Money</MenuItem>
                  <MenuItem value="bank">Bank</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                size="small"
                label="From Date"
                type="date"
                value={filters.from_date}
                onChange={(e) => handleFilterChange('from_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                size="small"
                label="To Date"
                type="date"
                value={filters.to_date}
                onChange={(e) => handleFilterChange('to_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <Select
                  value={filters.term_id}
                  label="Term"
                  onChange={(e) => handleFilterChange('term_id', e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">All Terms</MenuItem>
                  {terms.map((term) => (
                    <MenuItem key={term.id} value={term.id}>{term.name} {term.year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={2}>
              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                  fullWidth
                >
                  Clear Filters
                </Button>
              )}
            </Grid>
          </Grid>

          {hasActiveFilters && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>Active Filters:</strong>
              {filters.student_id && ` Student: ${students.find(s => s.id == filters.student_id)?.last_name} ${students.find(s => s.id == filters.student_id)?.first_name}`}
              {filters.payment_method && ` | Method: ${filters.payment_method.charAt(0).toUpperCase() + filters.payment_method.slice(1).replace('_', ' ')}`}
              {filters.from_date && ` | From: ${filters.from_date}`}
              {filters.to_date && ` | To: ${filters.to_date}`}
              {filters.term_id && ` | Term: ${terms.find(t => t.id == filters.term_id)?.name}`}
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Payment History</Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell>Fee Group</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Remarks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary" py={3}>
                        No payments found
                        {hasActiveFilters && " for the selected filters"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map((payment) => (
                    <TableRow key={payment.id} hover>
                      <TableCell>
                        {payment.student?.last_name} {payment.student?.first_name}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={payment.fee_group?.name || 'N/A'}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium" color="success.main">
                          UGX {payment.amount?.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={payment.payment_method?.replace('_', ' ').toUpperCase()}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(payment.payment_date).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </TableCell>
                      <TableCell>{payment.remarks || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Enhanced Pagination */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 2,
              mt: 3,
              pt: 2,
              borderTop: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Typography variant="body2" color="text.secondary">
                Showing {totalRecords === 0 ? 0 : (page - 1) * pageSize + 1} to{" "}
                {Math.min(page * pageSize, totalRecords)} of {totalRecords} entries
              </Typography>
              
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Rows per page:
                </Typography>
                <FormControl size="small">
                  <Select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    sx={{ minWidth: 70 }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={15}>15</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Stack>

            <Pagination
              count={totalPages}
              page={page}
              onChange={(event, newPage) => {
                setPage(newPage);
                fetchPayments(newPage, filters, pageSize);
              }}
              color="primary"
              size="medium"
              siblingCount={1}
              boundaryCount={1}
              showFirstButton
              showLastButton
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default FeePayments;